import React from 'react';
import { GenericModal } from '../../../../components/common/Modal/GenericModal';
import { useAddPaymentMethodMutation } from '../../../../../store/services/student/PaymentService';
import { showSuccessToast } from '../../../../components/PluginsMenu/Toastr/Toastr';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// ✅ Validation Schema with Yup
const PaymentSchema = Yup.object().shape({
  cardHolderName: Yup.string().trim()
    .required('Cardholder name is required')
    .matches(/^[A-Za-z\s]+$/, 'Card Holder name must contain only alphabets')
    .max(50, 'Cardholder name must be less than 50 characters'),
  cardNumber: Yup.string()
    .required('Card number is required')
    .matches(/^[0-9]{13,19}$/, 'Card number must be between 13-19 digits'),
  cvv: Yup.string()
    .required('CVV is required')
    .matches(/^[0-9]{3,4}$/, 'CVV must be 3 or 4 digits'),
  expiry: Yup.string()
    .required('Expiry date is required')
    .matches(/^\d{4}-(0[1-9]|1[0-2])$/, 'Invalid expiry format (MM-YYYY)'),
  isDefault: Yup.boolean(),
});

const AddPaymentMethodModal = ({ show, handleClose, onAddPaymentMethod }) => {
  const [addPaymentMethod, { isLoading }] = useAddPaymentMethodMutation();

  const handleSubmit = async (values, { resetForm, setSubmitting, setErrors }) => {
    try {
      const formattedExpiry = new Date(`${values.expiry}-01T00:00:00Z`).toISOString();

      const payload = {
        cardHolderName: values.cardHolderName,
        cardNumber: values.cardNumber,
        cvv: parseInt(values.cvv),
        expiry: formattedExpiry,
        isDefault: values.isDefault,
      };

      const response = await addPaymentMethod(payload).unwrap();

      if (response?.succeeded) {
        onAddPaymentMethod({
          id: response.data?.id,
          cardholderName: values.cardHolderName,
          last4: values.cardNumber.slice(-4),
          type: 'Custom',
          expiryDate: values.expiry,
          isDefault: values.isDefault,
        });

        resetForm();
        handleClose();
        showSuccessToast('Payment method added!');
      }
    } catch (err) {
      console.error('Failed to add card:', err);
      setErrors({ submit: err.data?.errors?.[0] || 'Failed to add payment method' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        cardHolderName: '',
        cardNumber: '',
        cvv: '',
        expiry: '',
        isDefault: false,
      }}
      validationSchema={PaymentSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, isSubmitting, errors }) => {
        const footerActions = (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit} // ✅ trigger Formik submit
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  Saving...
                </>
              ) : (
                'Add Card'
              )}
            </button>
          </>
        );

        return (
          <GenericModal
            show={show}
            handleClose={handleClose}
            title="Add New Payment Method"
            footerActions={footerActions}
          >
            <Form>
              {errors.submit && <div className="alert alert-danger">{errors.submit}</div>}

              <div className="mb-3">
                <label className="form-label">Cardholder Name</label>
                <Field type="text" name="cardHolderName" className="form-control" />
                <ErrorMessage name="cardHolderName" component="small" className="text-danger" />
              </div>

              <div className="mb-3">
                <label className="form-label">Card Number</label>
                <Field type="text" name="cardNumber" className="form-control" />
                <ErrorMessage name="cardNumber" component="small" className="text-danger" />
              </div>

              <div className="mb-3">
                <label className="form-label">CVV</label>
                <Field type="text" name="cvv" className="form-control" />
                <ErrorMessage name="cvv" component="small" className="text-danger" />
              </div>

              <div className="mb-3">
                <label className="form-label">Expiry (MM-YYYY)</label>
                <Field type="month" name="expiry" className="form-control" />
                <ErrorMessage name="expiry" component="small" className="text-danger" />
              </div>

              <div className="form-check mb-3">
                <Field type="checkbox" name="isDefault" className="form-check-input" id="defaultCheck" />
                <label className="form-check-label" htmlFor="defaultCheck">
                  Set as default
                </label>
              </div>
            </Form>
          </GenericModal>
        );
      }}
    </Formik>
  );
};

export default AddPaymentMethodModal;